import type { ElementType, ComponentPropsWithoutRef, ReactNode } from 'react'

export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 's1'
  | 's2'
  | 'b1'
  | 'b2'
  | 'b3'
  | 'b4'
  | 'caption'
  | 'btn-giant'
  | 'btn-large'
  | 'btn-medium'
  | 'btn-small'
  | 'btn-tiny'

const variantMap: Record<TypographyVariant, string> = {
  h1: 'text-h1',
  h2: 'text-h2',
  h3: 'text-h3',
  h4: 'text-h4',
  s1: 'text-s1',
  s2: 'text-s2',
  b1: 'text-b1',
  b2: 'text-b2',
  b3: 'text-b3',
  b4: 'text-b4',
  caption: 'text-caption',
  'btn-giant': 'text-btn-giant',
  'btn-large': 'text-btn-large',
  'btn-medium': 'text-btn-medium',
  'btn-small': 'text-btn-small',
  'btn-tiny': 'text-btn-tiny',
}

const defaultTagMap: Record<TypographyVariant, ElementType> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  s1: 'h5',
  s2: 'h6',
  b1: 'p',
  b2: 'p',
  b3: 'p',
  b4: 'span',
  caption: 'span',
  'btn-giant': 'span',
  'btn-large': 'span',
  'btn-medium': 'span',
  'btn-small': 'span',
  'btn-tiny': 'span',
}

const weightMap = {
  regular: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
}

const alignMap = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
}

export interface TypographyProps<T extends ElementType = 'span'> {
  variant?: TypographyVariant
  as?: T
  color?: string
  align?: 'left' | 'center' | 'right' | 'justify'
  truncate?: boolean
  weight?: 'regular' | 'medium' | 'semibold' | 'bold'
  className?: string
  children?: ReactNode
}

export function Typography<T extends ElementType = 'span'>({
  variant,
  as,
  color,
  align,
  truncate,
  weight,
  className = '',
  children,
  ...props
}: TypographyProps<T> & Omit<ComponentPropsWithoutRef<T>, keyof TypographyProps<T>>) {
  const Component = as || (variant ? defaultTagMap[variant] : 'span')
  
  const classes = [
    variant ? variantMap[variant] : '',
    weight ? weightMap[weight] : '',
    color || '',
    align ? alignMap[align] : '',
    truncate ? 'truncate block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Component className={classes} {...props}>
      {children}
    </Component>
  )
}
